import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ProjectFilters from './components/ProjectFilters';
import ProjectGrid from './components/ProjectGrid';
import ProjectModal from './components/ProjectModal';
import Icon from '../../components/AppIcon';

const PortfolioProjects = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedTech, setSelectedTech] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Mock project data
  const projects = [
    {
      id: 1,
      title: "E-Commerce Platform",
      type: "Web App",
      year: "2024",
      duration: "4 months",
      teamSize: "4 developers",
      role: "Full-Stack Developer",
      status: "Completed",
      rating: 4.8,
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop",
      description: "A comprehensive e-commerce platform with advanced features including real-time inventory management, payment processing, and analytics dashboard.",
      fullDescription: `A modern, scalable e-commerce platform built to handle high-traffic scenarios with seamless user experience. The platform features a responsive design, advanced search capabilities, and comprehensive admin dashboard for managing products, orders, and customer relationships.`,
      problemStatement: `Traditional e-commerce solutions were either too expensive for small businesses or lacked the scalability needed for growing companies. There was a need for a cost-effective, feature-rich platform that could grow with the business.`,
      solution: `Developed a modular e-commerce platform using React and Node.js with microservices architecture. Implemented real-time features using WebSocket connections and optimized performance with Redis caching and CDN integration.`,
      technologies: ["React", "Node.js", "MongoDB", "Express", "Tailwind", "AWS"],
      keyFeatures: [
        "Real-time inventory management",
        "Advanced product search and filtering",
        "Secure payment processing with Stripe",
        "Comprehensive analytics dashboard",
        "Mobile-responsive design",
        "SEO optimization"
      ],
      challenges: [
        {
          title: "Performance Optimization",
          solution: "Implemented lazy loading, code splitting, and Redis caching to achieve 95% performance score"
        },
        {
          title: "Payment Security",
          solution: "Integrated PCI-compliant payment processing with end-to-end encryption"
        }
      ],
      metrics: [
        { label: "Page Load Time", value: "< 2s" },
        { label: "Uptime", value: "99.9%" },
        { label: "User Satisfaction", value: "4.8/5" }
      ],
      liveUrl: "https://ecommerce-demo.example.com",
      githubUrl: "https://github.com/username/ecommerce-platform",
      screenshots: [
        "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop",
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop"
      ]
    },
    {
      id: 2,
      title: "Task Management App",
      type: "Web App",
      year: "2024",
      duration: "3 months",
      teamSize: "2 developers",
      role: "Frontend Lead",
      status: "Completed",
      rating: 4.6,
      image: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=600&h=400&fit=crop",
      description: "A collaborative task management application with real-time updates, team collaboration features, and advanced project tracking capabilities.",
      fullDescription: `A comprehensive task management solution designed for modern teams. Features include real-time collaboration, advanced project tracking, time management tools, and detailed analytics to help teams stay productive and organized.`,
      problemStatement: `Existing task management tools were either too complex for small teams or lacked the real-time collaboration features needed for remote work environments.`,
      solution: `Built a streamlined task management app with React and Firebase, focusing on real-time collaboration and intuitive user experience. Implemented drag-and-drop functionality and real-time notifications.`,
      technologies: ["React", "Firebase", "TypeScript", "Tailwind", "Node.js"],
      keyFeatures: [
        "Real-time task updates and notifications",
        "Drag-and-drop task management",
        "Team collaboration tools",
        "Time tracking and reporting",
        "Project templates and automation",
        "Mobile-responsive interface"
      ],
      challenges: [
        {
          title: "Real-time Synchronization",
          solution: "Implemented Firebase Firestore for real-time data synchronization across all connected clients"
        },
        {
          title: "Offline Functionality",
          solution: "Added service worker and local storage to enable offline task management"
        }
      ],
      metrics: [
        { label: "Active Users", value: "2,500+" },
        { label: "Task Completion Rate", value: "87%" },
        { label: "User Retention", value: "78%" }
      ],
      liveUrl: "https://taskmanager-demo.example.com",
      githubUrl: "https://github.com/username/task-manager",
      screenshots: [
        "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop"
      ]
    },
    {
      id: 3,
      title: "Weather Forecast API",
      type: "API",
      year: "2023",
      duration: "2 months",
      teamSize: "1 developer",
      role: "Backend Developer",
      status: "Completed",
      rating: 4.9,
      image: "https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=600&h=400&fit=crop",
      description: "A robust RESTful API providing accurate weather forecasts with caching, rate limiting, and comprehensive documentation.",
      fullDescription: `A high-performance weather API that aggregates data from multiple sources to provide accurate forecasts. Built with scalability in mind, featuring intelligent caching, rate limiting, and comprehensive error handling.`,
      problemStatement: `Existing weather APIs were either expensive, unreliable, or lacked the specific data points needed for agricultural and logistics applications.`,
      solution: `Developed a custom weather API using Node.js and Express, integrating multiple weather data sources with intelligent fallback mechanisms and advanced caching strategies.`,
      technologies: ["Node.js", "Express", "PostgreSQL", "Redis", "Docker"],
      keyFeatures: [
        "Multi-source weather data aggregation",
        "Intelligent caching with Redis",
        "Rate limiting and API key management",
        "Comprehensive API documentation",
        "Real-time weather alerts",
        "Historical weather data access"
      ],
      challenges: [
        {
          title: "Data Accuracy",
          solution: "Implemented weighted averaging algorithm across multiple weather data sources"
        },
        {
          title: "API Performance",
          solution: "Added Redis caching and database query optimization to achieve sub-100ms response times"
        }
      ],
      metrics: [
        { label: "API Calls/Day", value: "50K+" },
        { label: "Response Time", value: "< 100ms" },
        { label: "Uptime", value: "99.95%" }
      ],
      liveUrl: "https://weather-api-demo.example.com",
      githubUrl: "https://github.com/username/weather-api"
    },
    {
      id: 4,
      title: "Social Media Dashboard",
      type: "Web App",
      year: "2023",
      duration: "5 months",
      teamSize: "3 developers",
      role: "Full-Stack Developer",
      status: "Completed",
      rating: 4.7,
      image: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600&h=400&fit=crop",
      description: "An analytics dashboard for social media management with real-time metrics, content scheduling, and performance insights.",
      fullDescription: `A comprehensive social media management platform that helps businesses track performance across multiple social platforms, schedule content, and gain insights through advanced analytics and reporting.`,
      problemStatement: `Managing multiple social media accounts required switching between different platforms, making it difficult to maintain consistent branding and track overall performance.`,
      solution: `Created a unified dashboard using React and Node.js that integrates with major social media APIs, providing centralized management and comprehensive analytics in one place.`,
      technologies: ["React", "Node.js", "MongoDB", "Express", "GraphQL", "AWS"],
      keyFeatures: [
        "Multi-platform social media integration",
        "Content scheduling and automation",
        "Real-time analytics and reporting",
        "Team collaboration tools",
        "Brand monitoring and sentiment analysis",
        "Custom dashboard widgets"
      ],
      challenges: [
        {
          title: "API Rate Limiting",
          solution: "Implemented intelligent request queuing and caching to work within platform API limits"
        },
        {
          title: "Data Visualization",
          solution: "Created custom chart components using D3.js for interactive analytics dashboards"
        }
      ],
      metrics: [
        { label: "Accounts Managed", value: "10K+" },
        { label: "Posts Scheduled", value: "1M+" },
        { label: "Time Saved", value: "60%" }
      ],
      liveUrl: "https://social-dashboard-demo.example.com",
      githubUrl: "https://github.com/username/social-dashboard",
      screenshots: [
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop"
      ]
    },
    {
      id: 5,
      title: "Learning Management System",
      type: "Web App",
      year: "2023",
      duration: "6 months",
      teamSize: "5 developers",
      role: "Frontend Lead",
      status: "Completed",
      rating: 4.5,
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop",
      description: "A comprehensive LMS platform with course creation tools, student progress tracking, and interactive learning features.",
      fullDescription: `A modern learning management system designed for educational institutions and corporate training. Features include course authoring tools, student progress tracking, interactive assessments, and comprehensive reporting capabilities.`,
      problemStatement: `Traditional LMS platforms were outdated, difficult to use, and lacked the interactive features needed for modern online learning environments.`,
      solution: `Developed a modern LMS using React and Django, focusing on user experience and interactive learning features. Implemented video streaming, real-time collaboration, and advanced analytics.`,
      technologies: ["React", "Django", "PostgreSQL", "Python", "AWS", "Docker"],
      keyFeatures: [
        "Interactive course authoring tools",
        "Video streaming and playback",
        "Real-time student progress tracking",
        "Advanced assessment and grading",
        "Discussion forums and collaboration",
        "Mobile-responsive learning interface"
      ],
      challenges: [
        {
          title: "Video Streaming",
          solution: "Implemented adaptive bitrate streaming using AWS CloudFront for optimal video delivery"
        },
        {
          title: "Scalability",
          solution: "Used microservices architecture with Docker containers for horizontal scaling"
        }
      ],
      metrics: [
        { label: "Active Students", value: "15K+" },
        { label: "Courses Created", value: "500+" },
        { label: "Completion Rate", value: "82%" }
      ],
      liveUrl: "https://lms-demo.example.com",
      githubUrl: "https://github.com/username/lms-platform"
    },
    {
      id: 6,
      title: "Mobile Banking App",
      type: "Mobile App",
      year: "2022",
      duration: "8 months",
      teamSize: "6 developers",
      role: "Mobile Developer",
      status: "Completed",
      rating: 4.9,
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop",
      description: "A secure mobile banking application with biometric authentication, real-time transactions, and comprehensive financial management tools.",
      fullDescription: `A feature-rich mobile banking application that provides users with secure access to their financial accounts, real-time transaction capabilities, and comprehensive financial management tools. Built with security and user experience as top priorities.`,
      problemStatement: `Existing mobile banking apps had poor user experience, limited functionality, and security concerns that prevented widespread adoption among younger demographics.`,
      solution: `Developed a modern mobile banking app using React Native, implementing advanced security measures including biometric authentication, end-to-end encryption, and fraud detection algorithms.`,
      technologies: ["React", "Node.js", "PostgreSQL", "Express", "AWS"],
      keyFeatures: [
        "Biometric authentication (fingerprint/face)",
        "Real-time transaction processing",
        "Account balance and transaction history",
        "Bill payment and money transfer",
        "Financial insights and budgeting tools",
        "Push notifications for account activity"
      ],
      challenges: [
        {
          title: "Security Implementation",
          solution: "Implemented multi-layer security with biometric auth, encryption, and fraud detection"
        },
        {
          title: "Performance Optimization",
          solution: "Optimized app performance with efficient state management and lazy loading"
        }
      ],
      metrics: [
        { label: "App Store Rating", value: "4.9/5" },
        { label: "Daily Active Users", value: "25K+" },
        { label: "Transaction Volume", value: "$2M+/day" }
      ],
      liveUrl: "https://banking-app-demo.example.com",
      githubUrl: "https://github.com/username/banking-app"
    }
  ];

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Filter and sort projects
  const filteredAndSortedProjects = useMemo(() => {
    let filtered = projects;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(project =>
        project?.title?.toLowerCase()?.includes(query) ||
        project?.description?.toLowerCase()?.includes(query) ||
        project?.technologies?.some(tech => tech?.toLowerCase()?.includes(query))
      );
    }

    // Filter by project type
    if (selectedType !== 'all') {
      filtered = filtered?.filter(project => project?.type === selectedType);
    }

    // Filter by technologies
    if (selectedTech?.length > 0) {
      filtered = filtered?.filter(project =>
        selectedTech?.some(tech => project?.technologies?.includes(tech))
      );
    }

    // Sort projects
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return parseInt(b?.year) - parseInt(a?.year);
        case 'oldest':
          return parseInt(a?.year) - parseInt(b?.year);
        case 'rating':
          return b?.rating - a?.rating;
        case 'name':
          return a?.title?.localeCompare(b?.title);
        default:
          return 0;
      }
    });

    return filtered;
  }, [searchQuery, selectedType, selectedTech, sortBy]);

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedType('all');
    setSelectedTech([]);
    setSortBy('newest');
  };

  return (
    <>
      <Helmet>
        <title>Portfolio Projects - DevPortfolio Pro</title>
        <meta name="description" content="Explore my portfolio of web applications, mobile apps, and API projects. View detailed case studies, live demos, and source code." />
        <meta name="keywords" content="portfolio, projects, web development, mobile apps, API development, React, Node.js" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mr-4">
                <Icon name="Briefcase" size={24} className="text-primary" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                Portfolio Projects
              </h1>
            </div>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Explore my collection of web applications, mobile apps, and API projects. 
              Each project showcases different technologies and problem-solving approaches.
              Kindly send a dm to get a direct link to my project.
            </p>
          </div>

          {/* Filters */}
          <ProjectFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedType={selectedType}
            onTypeChange={setSelectedType}
            selectedTech={selectedTech}
            onTechChange={setSelectedTech}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onClearFilters={handleClearFilters}
            resultCount={filteredAndSortedProjects?.length}
          />

          {/* Projects Grid */}
          <ProjectGrid
            projects={filteredAndSortedProjects}
            onViewDetails={handleViewDetails}
            isLoading={isLoading}
          />

          {/* Project Modal */}
          <ProjectModal
            project={selectedProject}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
        </main>
      </div>
    </>
  );
};

export default PortfolioProjects;